import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Breadcrumb, Layout, theme, Alert, Spin, Empty, Card, Button, Tabs, Tag, Collapse, Typography } from "antd";
import {
    BulbOutlined,
    SearchOutlined,
    RocketOutlined,
    WarningOutlined,
    SwapOutlined,
    RobotOutlined,
    ReloadOutlined,
    LoadingOutlined,
    LockOutlined,
} from "@ant-design/icons";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useCalculateBrandEquityMutation, useGetBrandsQuery, useGetCohortsByBrandQuery, useGetAllAdviceInsightsWithDataMutation, useGetSingleAdviceInsightWithDataMutation, useGetSystemSettingQuery } from "../services/nestjs-base-api";
import { useMaturityMode } from "../hooks/useMaturityMode";
import { IBrandEquityScoreResponse, PeriodEnum } from "../services/nestjs-base-api/types";
import { DataSummaryCard, useDataSummary } from "../components/BrandEquity/DataSummaryCard";
import ReactMarkdown from 'react-markdown';
import { getDisplayName } from '../utils/elementDisplayNames';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// Track last fetched calculatedAt to prevent unnecessary refetches
let lastFetchedAdviceInsightsCalculatedAt: string | null = null;

// Score colour coding (matching DashboardPage thresholds, -100 to +100 scale)
const getScoreColor = (score: number): string => {
    if (score >= 70) return '#52c41a';
    if (score >= 60) return '#1890ff';
    if (score >= 40) return '#faad14';
    return '#ff4d4f';
};

const getScoreRating = (score: number, intl: any): string => {
    if (score >= 70) return intl.formatMessage({ id: 'advice.keyFindings.rating.excellent' });
    if (score >= 60) return intl.formatMessage({ id: 'advice.keyFindings.rating.good' });
    if (score >= 40) return intl.formatMessage({ id: 'advice.keyFindings.rating.fair' });
    return intl.formatMessage({ id: 'advice.keyFindings.rating.needsImprovement' });
};

const getPercentageColor = (value: number | undefined): string => {
    if (value === undefined) return '#8c8c8c';
    if (value >= 70) return '#52c41a';
    if (value >= 50) return '#1890ff';
    if (value >= 30) return '#faad14';
    return '#ff4d4f';
};

// Shared AI insight card component
const AIInsightCard: React.FC<{
    insight: { response: string; cached: boolean } | undefined;
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    title: string;
    icon: React.ReactNode;
    intl: any;
    isFreePlan?: boolean;
    navigate?: (path: string) => void;
    teaserWordCount?: number;
}> = ({ insight, isLoading, isRegenerating, onRegenerate, title, icon, intl, isFreePlan, navigate, teaserWordCount = 150 }) => {
    const markdownComponents = useMemo(() => ({
        p: ({children}: {children?: React.ReactNode}) => (
            <p style={{ margin: '0 0 8px 0', lineHeight: 1.6, color: '#374151' }}>{children}</p>
        ),
        strong: ({children}: {children?: React.ReactNode}) => (
            <strong style={{ fontWeight: 600, color: '#1f2937' }}>{children}</strong>
        ),
        ul: ({children}: {children?: React.ReactNode}) => (
            <ul style={{ margin: '4px 0 8px 0', paddingLeft: 20 }}>{children}</ul>
        ),
        ol: ({children}: {children?: React.ReactNode}) => (
            <ol style={{ margin: '4px 0 8px 0', paddingLeft: 20 }}>{children}</ol>
        ),
        li: ({children}: {children?: React.ReactNode}) => (
            <li style={{ marginBottom: 4, lineHeight: 1.5, color: '#374151' }}>{children}</li>
        ),
    }), []);

    // Truncate markdown for free plan users, preserving line breaks
    const displayMarkdown = useMemo(() => {
        if (!isFreePlan || !insight?.response) return insight?.response;
        const tokens = insight.response.split(/(\s+)/); // capture whitespace including newlines
        let wordCount = 0;
        let result = '';
        for (const token of tokens) {
            if (/\S/.test(token)) {
                wordCount++;
                if (wordCount > teaserWordCount) break;
            }
            result += token;
        }
        if (wordCount > teaserWordCount) result += '...';
        return result;
    }, [isFreePlan, insight?.response, teaserWordCount]);

    const totalWords = useMemo(() => {
        if (!insight?.response) return 0;
        return insight.response.split(/\s+/).length;
    }, [insight?.response]);

    return (
        <Card style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>
                    {icon}
                    <span style={{ marginLeft: 8 }}>{title}</span>
                </Title>
                {!isFreePlan && (
                    <Button
                        size="small"
                        icon={(isLoading || isRegenerating) ? <LoadingOutlined /> : <ReloadOutlined />}
                        onClick={onRegenerate}
                        disabled={isLoading || isRegenerating}
                    >
                        {isRegenerating
                            ? intl.formatMessage({ id: 'advice.ai.regenerating' })
                            : intl.formatMessage({ id: 'advice.ai.regenerate' })}
                    </Button>
                )}
            </div>

            {(isLoading || isRegenerating) && !insight && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <Spin size="small" />
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        {intl.formatMessage({ id: 'advice.ai.loading' })}
                    </Text>
                </div>
            )}

            {insight?.response && insight.response !== 'Analysis unavailable' && (
                <>
                    <div style={{ position: 'relative' }}>
                        <div style={{
                            padding: 16,
                            background: '#f8f9ff',
                            borderRadius: 8,
                            border: '1px solid #e8ecff',
                            opacity: isRegenerating ? 0.4 : 1,
                            transition: 'opacity 0.3s ease',
                        }}>
                            <ReactMarkdown components={markdownComponents}>
                                {displayMarkdown || ''}
                            </ReactMarkdown>
                        </div>
                        {isFreePlan && (
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 120,
                                background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.8) 40%, white 100%)',
                                borderRadius: '0 0 8px 8px',
                                pointerEvents: 'none',
                            }} />
                        )}
                    </div>
                    {isFreePlan && (
                        <div style={{
                            marginTop: 16,
                            padding: '20px 24px',
                            background: '#fffbf0',
                            border: '1px solid #F59E0B',
                            borderRadius: 8,
                            textAlign: 'center',
                        }}>
                            <LockOutlined style={{ fontSize: 28, color: '#F59E0B', marginBottom: 8 }} />
                            <div style={{ marginBottom: 4 }}>
                                <Text strong style={{ fontSize: 15 }}>Upgrade to unlock full AI analysis</Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Reading {Math.min(teaserWordCount, totalWords)} of ~{totalWords} words
                            </Text>
                            <div style={{ marginTop: 12 }}>
                                <Button
                                    type="primary"
                                    icon={<LockOutlined />}
                                    onClick={() => navigate?.('/dashboard/organisation?tab=billing&showUpgrade=true')}
                                    style={{
                                        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                        border: 'none',
                                    }}
                                >
                                    Upgrade
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {insight?.response === 'Analysis unavailable' && (
                <Alert
                    message={intl.formatMessage({ id: 'advice.ai.error' })}
                    type="warning"
                    showIcon
                />
            )}
        </Card>
    );
};

// ==================== TAB COMPONENTS ====================

interface KeyFindingsTabProps {
    data: IBrandEquityScoreResponse;
    intl: any;
    insight?: { response: string; cached: boolean };
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    isFreePlan?: boolean;
    navigate?: (path: string) => void;
    teaserWordCount?: number;
}

const KeyFindingsTab: React.FC<KeyFindingsTabProps> = ({ data, intl, insight, isLoading, isRegenerating, onRegenerate, isFreePlan, navigate, teaserWordCount }) => {
    return (
        <div>
            {/* AI Summary */}
            <AIInsightCard
                insight={insight}
                isLoading={isLoading}
                isRegenerating={isRegenerating}
                onRegenerate={onRegenerate}
                title={intl.formatMessage({ id: 'advice.keyFindings.aiSummary.title' })}
                icon={<RobotOutlined />}
                intl={intl}
                isFreePlan={isFreePlan}
                navigate={navigate}
                teaserWordCount={teaserWordCount}
            />
        </div>
    );
};

interface ExploreFurtherTabProps {
    data: IBrandEquityScoreResponse;
    intl: any;
    navigate: (path: string) => void;
    insight?: { response: string; cached: boolean };
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    isFreePlan?: boolean;
    teaserWordCount?: number;
}

const ExploreFurtherTab: React.FC<ExploreFurtherTabProps> = ({ data, intl, navigate, insight, isLoading, isRegenerating, onRegenerate, isFreePlan, teaserWordCount }) => {
    const categories = useMemo(() => {
        return [...data.questionCategoryScores]
            .filter(c => c.meanScore !== null)
            .sort((a, b) => (b.meanScore || 0) - (a.meanScore || 0));
    }, [data.questionCategoryScores]);

    const handleDiscussWithAi = (statementText: string, metrics: {
        meanScore: number | null;
        intensityPercentage?: number;
        brandPowerPercentage?: number;
        controversyPercentage?: number;
    }) => {
        const prompt = `I'd like to discuss this brand equity statement: "${statementText}"\n\nMetrics:\n- Score: ${metrics.meanScore?.toFixed(1) ?? 'N/A'}%${metrics.intensityPercentage !== undefined ? `\n- Intensity: ${metrics.intensityPercentage.toFixed(1)}%` : ''}${metrics.brandPowerPercentage !== undefined ? `\n- Brand Power: ${metrics.brandPowerPercentage.toFixed(1)}%` : ''}${metrics.controversyPercentage !== undefined ? `\n- Controversy: ${metrics.controversyPercentage.toFixed(1)}%` : ''}\n\nWhat insights can you provide about this result and what actions might improve it?`;
        navigate(`/dashboard/ai-experts?expert=data_analyst_expert&prompt=${encodeURIComponent(prompt)}`);
    };

    return (
        <div>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                {intl.formatMessage({ id: 'advice.exploreFurther.description' })}
            </Paragraph>

            {/* AI Overview */}
            <AIInsightCard
                insight={insight}
                isLoading={isLoading}
                isRegenerating={isRegenerating}
                onRegenerate={onRegenerate}
                title="AI Research Analysis"
                icon={<RobotOutlined />}
                intl={intl}
                isFreePlan={isFreePlan}
                navigate={navigate}
                teaserWordCount={teaserWordCount}
            />

            <div style={{ marginTop: 24 }}>
                {categories.length > 0 ? (
                    <Collapse
                        accordion
                        items={categories.map((cat, idx) => ({
                            key: String(idx),
                            label: (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 16 }}>
                                    <span>
                                        <Text strong>{getDisplayName(cat.category)}</Text>
                                    </span>
                                    <span>
                                        <Tag color={getScoreColor(cat.meanScore || 0)} style={{ marginLeft: 12 }}>
                                            {intl.formatMessage({ id: 'advice.exploreFurther.meanScore' })}: {cat.meanScore?.toFixed(2)}
                                        </Tag>
                                        <Tag style={{ marginLeft: 4 }}>
                                            {intl.formatMessage({ id: 'advice.exploreFurther.responses' })}: {cat.responseCount}
                                        </Tag>
                                    </span>
                                </div>
                            ),
                            children: (
                                <div>
                                    {cat.aiSummary && (
                                        <Alert
                                            message={cat.aiSummary}
                                            type="info"
                                            showIcon
                                            icon={<RobotOutlined />}
                                            style={{ marginBottom: 16 }}
                                        />
                                    )}
                                    {cat.input && cat.input.length > 0 ? (
                                        <div>
                                            {cat.input.map((statement, sIdx) => (
                                                <div key={sIdx} style={{
                                                    padding: '12px 16px',
                                                    background: '#fafafa',
                                                    borderRadius: 8,
                                                    border: '1px solid #f0f0f0',
                                                    marginBottom: 8,
                                                }}>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Text>{statement.text}</Text>
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                                        <Tag color={getScoreColor(statement.meanScore ?? 0)}>
                                                            Score: {statement.meanScore?.toFixed(2) ?? 'N/A'}
                                                        </Tag>
                                                        {statement.meanScore != null && (
                                                            <Tag color={getPercentageColor(statement.meanScore)}>
                                                                {intl.formatMessage({ id: 'advice.exploreFurther.polarity' })}: {statement.meanScore.toFixed(1)}%
                                                            </Tag>
                                                        )}
                                                        {statement.intensityPercentage != null && (
                                                            <Tag color={getPercentageColor(statement.intensityPercentage)}>
                                                                {intl.formatMessage({ id: 'advice.exploreFurther.intensity' })}: {statement.intensityPercentage.toFixed(1)}%
                                                            </Tag>
                                                        )}
                                                        {statement.brandPowerPercentage != null && (
                                                            <Tag color={getPercentageColor(statement.brandPowerPercentage)}>
                                                                {intl.formatMessage({ id: 'advice.exploreFurther.brandPower' })}: {statement.brandPowerPercentage.toFixed(1)}%
                                                            </Tag>
                                                        )}
                                                        {statement.controversyPercentage != null && (
                                                            <Tag color={getPercentageColor(statement.controversyPercentage)}>
                                                                {intl.formatMessage({ id: 'advice.exploreFurther.controversy' })}: {statement.controversyPercentage.toFixed(1)}%
                                                            </Tag>
                                                        )}
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            icon={<RobotOutlined />}
                                                            onClick={() => handleDiscussWithAi(statement.text, {
                                                                meanScore: statement.meanScore,
                                                                intensityPercentage: statement.intensityPercentage,
                                                                brandPowerPercentage: statement.brandPowerPercentage,
                                                                controversyPercentage: statement.controversyPercentage,
                                                            })}
                                                            style={{ marginLeft: 'auto' }}
                                                        >
                                                            {intl.formatMessage({ id: 'advice.exploreFurther.discussWithAi' })}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Text type="secondary">
                                            {intl.formatMessage({ id: 'advice.exploreFurther.noStatements' })}
                                        </Text>
                                    )}
                                </div>
                            ),
                        }))}
                    />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'advice.exploreFurther.noCategories' })}
                            </Text>
                        }
                    />
                )}
            </div>
        </div>
    );
};

interface OpportunitiesTabProps {
    data: IBrandEquityScoreResponse | null;
    intl: any;
    insight?: { response: string; cached: boolean };
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    isFreePlan?: boolean;
    navigate?: (path: string) => void;
    teaserWordCount?: number;
}

const OpportunitiesTab: React.FC<OpportunitiesTabProps> = ({ data, intl, insight, isLoading, isRegenerating, onRegenerate, isFreePlan, navigate, teaserWordCount }) => {
    return (
        <div>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                {intl.formatMessage({ id: 'advice.opportunities.description' })}
            </Paragraph>

            <AIInsightCard
                insight={insight}
                isLoading={isLoading}
                isRegenerating={isRegenerating}
                onRegenerate={onRegenerate}
                title="AI Growth Analysis"
                icon={<RocketOutlined />}
                intl={intl}
                isFreePlan={isFreePlan}
                navigate={navigate}
                teaserWordCount={teaserWordCount}
            />
        </div>
    );
};

interface ThreatsTabProps {
    data: IBrandEquityScoreResponse | null;
    intl: any;
    insight?: { response: string; cached: boolean };
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    isFreePlan?: boolean;
    navigate?: (path: string) => void;
    teaserWordCount?: number;
}

const ThreatsTab: React.FC<ThreatsTabProps> = ({ data, intl, insight, isLoading, isRegenerating, onRegenerate, isFreePlan, navigate, teaserWordCount }) => {
    return (
        <div>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                {intl.formatMessage({ id: 'advice.threats.description' })}
            </Paragraph>

            <AIInsightCard
                insight={insight}
                isLoading={isLoading}
                isRegenerating={isRegenerating}
                onRegenerate={onRegenerate}
                title="AI Risk Analysis"
                icon={<WarningOutlined />}
                intl={intl}
                isFreePlan={isFreePlan}
                navigate={navigate}
                teaserWordCount={teaserWordCount}
            />
        </div>
    );
};

interface NotableChangesTabProps {
    intl: any;
    insight?: { response: string; cached: boolean };
    isLoading: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
    isFreePlan?: boolean;
    navigate?: (path: string) => void;
    teaserWordCount?: number;
}

const NotableChangesTab: React.FC<NotableChangesTabProps> = ({ intl, insight, isLoading, isRegenerating, onRegenerate, isFreePlan, navigate, teaserWordCount }) => {
    return (
        <div>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                {intl.formatMessage({ id: 'advice.notableChanges.description' })}
            </Paragraph>

            {/* AI Analysis */}
            <AIInsightCard
                insight={insight}
                isLoading={isLoading}
                isRegenerating={isRegenerating}
                onRegenerate={onRegenerate}
                title="AI Trend Analysis"
                icon={<SwapOutlined />}
                intl={intl}
                isFreePlan={isFreePlan}
                navigate={navigate}
                teaserWordCount={teaserWordCount}
            />
        </div>
    );
};

// ==================== MAIN PAGE ====================

const AdvicePage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const [activeTabKey, setActiveTabKey] = useState<string | undefined>(tabParam || undefined);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Beta mode settings
    const { isBetaMode, betaMessage } = useMaturityMode();
    const { data: teaserWordCountData } = useGetSystemSettingQuery(
        { jwtToken: user?.jwtToken || '', settingName: 'ADVICE_TEASER_WORD_COUNT' },
        { skip: !user?.jwtToken }
    );
    const teaserWordCount = (teaserWordCountData && teaserWordCountData !== 'NOT_FOUND')
        ? parseInt(teaserWordCountData as string, 10) || 150
        : 150;

    const [brandEquityData, setBrandEquityData] = useState<IBrandEquityScoreResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [calculateBrandEquity] = useCalculateBrandEquityMutation();

    // Advice AI insights
    const [getAdviceInsights, { data: adviceInsights, isLoading: insightsLoading }] = useGetAllAdviceInsightsWithDataMutation();
    const [getSingleAdviceInsight] = useGetSingleAdviceInsightWithDataMutation();
    const [loadingExperts, setLoadingExperts] = useState<Set<string>>(new Set());
    const [updatedInsights, setUpdatedInsights] = useState<{ [expertType: string]: { response: string; cached: boolean } }>({});

    // Cache insights in localStorage
    const ADVICE_INSIGHTS_STORAGE_KEY = `advice-insights-${user?.organisation?.id}`;
    const [cachedInsights, setCachedInsights] = useState<any>(null);

    // Load cached insights on mount
    useEffect(() => {
        const saved = localStorage.getItem(ADVICE_INSIGHTS_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setCachedInsights(parsed.insights);
                if (parsed.calculatedAt) {
                    lastFetchedAdviceInsightsCalculatedAt = parsed.calculatedAt;
                }
            } catch (e) {
                console.error('Failed to parse cached advice insights', e);
            }
        }
    }, [ADVICE_INSIGHTS_STORAGE_KEY]);

    // Save insights to localStorage when they arrive
    useEffect(() => {
        if (brandEquityData?.calculatedAt && (adviceInsights || Object.keys(updatedInsights).length > 0)) {
            const mergedInsights = { ...(adviceInsights || cachedInsights), ...updatedInsights };
            if (Object.keys(mergedInsights).length > 0) {
                localStorage.setItem(ADVICE_INSIGHTS_STORAGE_KEY, JSON.stringify({
                    insights: mergedInsights,
                    calculatedAt: brandEquityData.calculatedAt
                }));
                setCachedInsights(mergedInsights);
            }
        }
    }, [adviceInsights, updatedInsights, brandEquityData?.calculatedAt, cachedInsights, ADVICE_INSIGHTS_STORAGE_KEY]);

    // Fetch advice insights when brand equity data is available
    useEffect(() => {
        if (user?.jwtToken && brandEquityData) {
            if (brandEquityData.insufficientSampleSize) return;

            const calculatedAt = brandEquityData.calculatedAt;
            if (lastFetchedAdviceInsightsCalculatedAt === calculatedAt) {
                return;
            }
            lastFetchedAdviceInsightsCalculatedAt = calculatedAt;

            setUpdatedInsights({});
            getAdviceInsights({
                jwtToken: user.jwtToken,
                brandEquityData: brandEquityData,
                forceRefresh: false
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.jwtToken, brandEquityData]);

    // Function to refresh a single expert
    const refreshSingleExpert = async (expertType: string) => {
        if (!user?.jwtToken || !brandEquityData) return;
        if (brandEquityData.insufficientSampleSize) return;

        setLoadingExperts(prev => new Set(Array.from(prev).concat(expertType)));

        try {
            const result = await getSingleAdviceInsight({
                jwtToken: user.jwtToken,
                expertType,
                brandEquityData: brandEquityData,
                forceRefresh: true
            }).unwrap();

            setUpdatedInsights(prev => ({
                ...prev,
                [expertType]: result
            }));
        } catch (error) {
            console.error(`Error refreshing advice ${expertType}:`, error);
        } finally {
            setLoadingExperts(prev => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(expertType);
                return newSet;
            });
        }
    };

    // Merge insights
    const currentInsights = { ...(adviceInsights || cachedInsights), ...updatedInsights };

    // Load brands for display
    const { data: brandsData } = useGetBrandsQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: user?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !user?.organisation?.id }
    );

    // Subscription check for paywall
    const firstBrandId = brandsData?.[0]?.id;
    const { hasActiveSubscription, plan, bypassEnabled, isLoading: subscriptionLoading } = useSubscriptionFeatures(firstBrandId);
    // Don't treat as free while subscription data is still loading
    // Paid = any active subscription that isn't the Free tier (includes Professional, Enterprise, Founding, etc.)
    const isFreePlan = !subscriptionLoading && !bypassEnabled && (!hasActiveSubscription || !plan || (plan as string) === 'Free');

    // Get filter info
    const getFilterInfo = () => {
        if (!user?.id) return null;
        const filterKey = `brandEquityFilters_${user.id}`;
        const savedFilters = localStorage.getItem(filterKey);
        if (!savedFilters) return null;
        try {
            return JSON.parse(savedFilters);
        } catch (e) {
            return null;
        }
    };

    const filterInfo = getFilterInfo();

    // Load cohorts for the selected brand
    const { data: cohortsData } = useGetCohortsByBrandQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: user?.organisation?.id || 0,
            brandId: filterInfo?.brandId || 0
        },
        { skip: !user?.jwtToken || !user?.organisation?.id || !filterInfo?.brandId }
    );

    const dataSummary = useDataSummary({ filterInfo, brandsData, cohortsData });

    const loadBrandEquityData = useCallback(async () => {
        if (!user?.organisation?.id || !user?.jwtToken) return;

        try {
            setIsLoading(true);
            setError(null);

            // Check for cached data first
            const cacheKey = `brandEquityResults_${user.id}`;
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < 5 * 60 * 1000) {
                    setBrandEquityData(data);
                    setIsLoading(false);
                    return;
                }
            }

            // Get filters from localStorage
            const filterKey = `brandEquityFilters_${user.id}`;
            const savedFilters = localStorage.getItem(filterKey);
            let filters: { brandIds?: number[], dateFrom?: string, dateTo?: string, cohortIds?: number[], countryCodes?: string[], period?: PeriodEnum } = {};

            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);

                // Migrate old filters
                if (parsed.surveyIds) {
                    delete parsed.surveyIds;
                    parsed.cohortIds = undefined;
                }
                delete parsed.filterMode;

                if (parsed.periodType && parsed.periodType !== 'custom') {
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };

                    switch (parsed.periodType) {
                        case 'last30':
                            filters.period = PeriodEnum.THIRTY_DAYS;
                            break;
                        case 'last60':
                            filters.period = PeriodEnum.SIXTY_DAYS;
                            break;
                        case 'last90':
                            filters.period = PeriodEnum.NINETY_DAYS;
                            break;
                        case 'last120':
                            filters.period = PeriodEnum.ONE_TWENTY_DAYS;
                            break;
                        case 'last180':
                            filters.period = PeriodEnum.ONE_EIGHTY_DAYS;
                            break;
                        case 'last365':
                            filters.period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                            break;
                        case 'allTime':
                            filters.period = PeriodEnum.ALL_TIME;
                            break;
                        default:
                            filters.period = PeriodEnum.THIRTY_DAYS;
                    }
                } else if (parsed.periodType === 'custom' && parsed.dateFrom && parsed.dateTo) {
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        period: PeriodEnum.DATE_RANGE,
                        dateFrom: parsed.dateFrom,
                        dateTo: parsed.dateTo,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                } else if (parsed.dateFrom && parsed.dateTo) {
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        period: PeriodEnum.DATE_RANGE,
                        dateFrom: parsed.dateFrom,
                        dateTo: parsed.dateTo,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                } else {
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                }
            }

            if (!filters.period && !filters.dateFrom && !filters.dateTo) {
                filters = {
                    brandIds: filters.brandIds || undefined,
                    period: PeriodEnum.THIRTY_DAYS,
                    cohortIds: undefined
                };
            }

            const result = await calculateBrandEquity({
                jwtToken: user.jwtToken,
                organisationId: user.organisation.id,
                payload: {
                    ...filters,
                    skipAiAnalysis: true
                }
            }).unwrap();

            setBrandEquityData(result);

            // Cache the results
            localStorage.setItem(`brandEquityResults_${user.id}`, JSON.stringify({
                data: result,
                timestamp: Date.now()
            }));

        } catch (err: any) {
            console.error("Advice data loading failed:", err);

            if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
                const errorMessage = err?.data?.message || '';
                if (errorMessage && (errorMessage.includes('subscription') || errorMessage.includes('Professional'))) {
                    navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
                    return;
                } else {
                    setError(err?.data?.message || intl.formatMessage({ id: 'advice.error.loading' }));
                }
            } else {
                setError(err?.data?.message || intl.formatMessage({ id: 'advice.error.loading' }));
            }
        } finally {
            setIsLoading(false);
        }
    }, [user, calculateBrandEquity, intl, navigate]);

    useEffect(() => {
        loadBrandEquityData();
    }, [loadBrandEquityData]);

    // Smooth scroll to tabs when navigated with #tabs hash or ?tab= param
    useEffect(() => {
        if ((location.hash === '#tabs' || tabParam) && brandEquityData && !brandEquityData.insufficientSampleSize) {
            const el = document.getElementById('advice-tabs');
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, [location.hash, tabParam, brandEquityData]);

    const tabItems = useMemo(() => [
        {
            key: 'key-findings',
            label: (
                <span>
                    <BulbOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.tab.keyFindings' })}
                </span>
            ),
            children: brandEquityData ? (
                <KeyFindingsTab
                    data={brandEquityData}
                    intl={intl}
                    insight={currentInsights?.advice_key_findings}
                    isLoading={insightsLoading}
                    isRegenerating={loadingExperts.has('advice_key_findings')}
                    onRegenerate={() => refreshSingleExpert('advice_key_findings')}
                    isFreePlan={isFreePlan}
                    navigate={navigate}
                    teaserWordCount={teaserWordCount}
                />
            ) : null,
        },
        {
            key: 'explore-further',
            label: (
                <span>
                    <SearchOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.tab.exploreFurther' })}
                </span>
            ),
            children: brandEquityData ? (
                <ExploreFurtherTab
                    data={brandEquityData}
                    intl={intl}
                    navigate={navigate}
                    insight={currentInsights?.advice_explore_further}
                    isLoading={insightsLoading}
                    isRegenerating={loadingExperts.has('advice_explore_further')}
                    onRegenerate={() => refreshSingleExpert('advice_explore_further')}
                    isFreePlan={isFreePlan}
                    teaserWordCount={teaserWordCount}
                />
            ) : null,
        },
        {
            key: 'opportunities',
            label: (
                <span>
                    <RocketOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.tab.opportunities' })}
                </span>
            ),
            children: (
                <OpportunitiesTab
                    data={brandEquityData}
                    intl={intl}
                    insight={currentInsights?.advice_opportunities}
                    isLoading={insightsLoading}
                    isRegenerating={loadingExperts.has('advice_opportunities')}
                    onRegenerate={() => refreshSingleExpert('advice_opportunities')}
                    isFreePlan={isFreePlan}
                    navigate={navigate}
                    teaserWordCount={teaserWordCount}
                />
            ),
        },
        {
            key: 'threats',
            label: (
                <span>
                    <WarningOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.tab.threats' })}
                </span>
            ),
            children: (
                <ThreatsTab
                    data={brandEquityData}
                    intl={intl}
                    insight={currentInsights?.advice_threats}
                    isLoading={insightsLoading}
                    isRegenerating={loadingExperts.has('advice_threats')}
                    onRegenerate={() => refreshSingleExpert('advice_threats')}
                    isFreePlan={isFreePlan}
                    navigate={navigate}
                    teaserWordCount={teaserWordCount}
                />
            ),
        },
        {
            key: 'notable-changes',
            label: (
                <span>
                    <SwapOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.tab.notableChanges' })}
                </span>
            ),
            children: (
                <NotableChangesTab
                    intl={intl}
                    insight={currentInsights?.advice_notable_changes}
                    isLoading={insightsLoading}
                    isRegenerating={loadingExperts.has('advice_notable_changes')}
                    onRegenerate={() => refreshSingleExpert('advice_notable_changes')}
                    isFreePlan={isFreePlan}
                    navigate={navigate}
                    teaserWordCount={teaserWordCount}
                />
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [brandEquityData, intl, navigate, currentInsights, insightsLoading, loadingExperts, isFreePlan, teaserWordCount]);

    return (
        <Content style={{ margin: "0 16px" }}>
            <Breadcrumb
                style={{ margin: "16px 0" }}
                items={[
                    { title: intl.formatMessage({ id: 'advice.breadcrumb.intelligence' }) },
                    { title: intl.formatMessage({ id: 'advice.breadcrumb.advice' }) },
                ]}
            />
            <div
                style={{
                    padding: 24,
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Title level={3}>
                    <BulbOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'advice.title' })}
                    {isBetaMode && (
                        <Tag color="purple" style={{ marginLeft: 8, verticalAlign: 'middle', fontSize: 12 }}>
                            {intl.formatMessage({ id: 'advice.beta.tag' })}
                        </Tag>
                    )}
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: isBetaMode ? 16 : 24 }}>
                    {intl.formatMessage({ id: 'advice.description' })}
                </Paragraph>
                {isBetaMode && (
                    <Alert
                        message={betaMessage}
                        type="info"
                        showIcon
                        style={{
                            marginBottom: 24,
                            background: '#f9f0ff',
                            border: '1px solid #d3adf7',
                        }}
                    />
                )}

                {/* Data Summary */}
                {brandEquityData && (
                    <DataSummaryCard
                        brandName={dataSummary.brandName !== 'Unknown Brand' ? dataSummary.brandName : (brandsData?.[0]?.name || 'Brand')}
                        periodDescription={dataSummary.periodDescription}
                        cohortNames={dataSummary.cohortNames}
                        countryNames={dataSummary.countryNames}
                        totalResponses={brandEquityData.totalResponses}
                        calculatedAt={brandEquityData.calculatedAt}
                        changeFiltersLink="/dashboard/analytics/data-selection?returnTo=advice"
                    />
                )}

                {/* Loading State */}
                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>
                            {intl.formatMessage({ id: 'advice.loading' })}
                        </p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <Alert
                        message={intl.formatMessage({ id: 'advice.error.title' })}
                        description={error}
                        type="error"
                        closable
                        onClose={() => setError(null)}
                        style={{ marginBottom: 24 }}
                    />
                )}

                {/* Empty State - no data configured */}
                {!isLoading && !error && !brandEquityData && (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <Title level={5} type="secondary">
                                        {intl.formatMessage({ id: 'advice.empty.title' })}
                                    </Title>
                                    <Paragraph type="secondary">
                                        {intl.formatMessage({ id: 'advice.empty.description' })}
                                    </Paragraph>
                                </div>
                            }
                        >
                            <Button
                                type="primary"
                                onClick={() => navigate('/dashboard/analytics/data-selection?returnTo=advice')}
                            >
                                {intl.formatMessage({ id: 'advice.empty.goToDataSelection' })}
                            </Button>
                        </Empty>
                    </div>
                )}

                {/* Insufficient sample size */}
                {!isLoading && !error && brandEquityData?.insufficientSampleSize && (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Card style={{ maxWidth: 500, margin: '0 auto' }}>
                            <WarningOutlined style={{ fontSize: 36, color: '#faad14', marginBottom: 16 }} />
                            <Title level={5}>
                                {intl.formatMessage({ id: 'advice.insufficientData.title' })}
                            </Title>
                            <Paragraph type="secondary">
                                {intl.formatMessage(
                                    { id: 'advice.insufficientData.description' },
                                    { count: brandEquityData.totalResponses }
                                )}
                            </Paragraph>
                            <Button
                                type="primary"
                                onClick={() => navigate('/dashboard/surveys')}
                            >
                                {intl.formatMessage({ id: 'advice.insufficientData.manageSurveys' })}
                            </Button>
                        </Card>
                    </div>
                )}

                {/* Tabs - show when data is available and sufficient */}
                {!isLoading && !error && brandEquityData && !brandEquityData.insufficientSampleSize && (
                    <Tabs
                        id="advice-tabs"
                        defaultActiveKey="key-findings"
                        activeKey={activeTabKey}
                        onChange={(key) => setActiveTabKey(key)}
                        items={tabItems}
                        size="large"
                    />
                )}
            </div>
        </Content>
    );
};

export default AdvicePage;
